import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { chunkErrorAction, isChunkLoadError, manualReload } from './chunkReload';

/**
 * Route-level error boundary for the whole authenticated app. Wired as the
 * `errorElement` of the `/` route (see router.tsx), so it REPLACES React
 * Router's default "Unexpected Application Error! / Hey developer" screen —
 * which leaks framework internals, in English only, with no way back — for any
 * error thrown anywhere under the shell, lazy-import failures included.
 *
 * Two jobs:
 *  1. Stale chunk → drive the one-shot automatic reload (chunkReload.ts). While
 *     that reload is in flight we show a neutral placeholder, never the error
 *     screen, so a routine deploy doesn't flash a scary message on the way to
 *     recovering itself.
 *  2. Anything else (or the second chunk failure) → the branded error screen,
 *     with a way back and no leaked detail.
 */

const HOME_HREF = import.meta.env.BASE_URL || '/';

function ReloadingPlaceholder() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-raad-bg text-raad-muted">
      <RaadLogo size={48} title={t('brand.name')} />
      <p className="text-sm" role="status">
        {t('app.loading')}
      </p>
    </div>
  );
}

/**
 * The real error, ON SCREEN, in DEV ONLY.
 *
 * "Something went wrong" is the right thing to show a customer — a stack trace
 * on a fleet manager's phone is noise he cannot act on, and a module URL is
 * information he should not have. But it is the WRONG thing to show a developer,
 * and slice 12 paid for that: the geofence draw tools threw
 * `Cannot read properties of undefined (reading 'setData')` and the screen said
 * only "Something went wrong", so the cause was invisible to the person using
 * the app AND to the agent debugging it. It took a headed browser and a console
 * capture to learn a single sentence the app already knew.
 *
 * `import.meta.env.DEV` is statically replaced by Vite and this whole block is
 * dropped from the production bundle, so the customer-facing guarantee above is
 * unchanged — asserted by a test, not assumed.
 */
function DevErrorDetail({ error }: { error: unknown }) {
  if (!import.meta.env.DEV) return null;
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  return (
    <details
      data-testid="dev-error-detail"
      className="mt-5 w-full max-w-2xl rounded-lg border border-raad-border bg-raad-inset p-3 text-left"
      open
    >
      <summary className="cursor-pointer text-xs font-semibold text-raad-accent">
        dev only — the actual error
      </summary>
      <p className="mt-2 break-words font-mono text-xs text-raad-text">{message}</p>
      {stack ? (
        <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-raad-muted">
          {stack}
        </pre>
      ) : null}
    </details>
  );
}

function ErrorScreen({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-raad-bg px-6 text-center text-raad-text">
      <RaadLogo size={44} />
      <h1 className="mt-6 font-display text-xl font-bold">{t('errorBoundary.title')}</h1>
      <p className="mt-2 max-w-sm text-sm text-raad-muted">{t('errorBoundary.body')}</p>
      <DevErrorDetail error={error} />
      <div className="mt-7 flex w-full max-w-xs flex-col items-stretch gap-2.5">
        {/* Primary: a FULL reload. It fetches a fresh index.html and therefore
            the new chunk names — the one action that actually fixes the common
            cause. Not a soft retry against the same broken shell in memory.
            Never guarded: this deliberate press must work every time. */}
        <button
          type="button"
          onClick={manualReload}
          className="flex min-h-12 items-center justify-center rounded-xl bg-raad-primary px-5 text-sm font-bold text-raad-onprimary transition-opacity hover:opacity-95"
        >
          {t('errorBoundary.reload')}
        </button>
        {/* Secondary: a hard navigation home — also a fresh document load, so it
            recovers a stale chunk too, and lands on the non-lazy overview. */}
        <a
          href={HOME_HREF}
          className="flex min-h-12 items-center justify-center rounded-xl border border-raad-border bg-raad-panel px-5 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-20"
        >
          {t('errorBoundary.home')}
        </a>
      </div>
    </main>
  );
}

export function RouteError() {
  const error = useRouteError();
  const chunk = isChunkLoadError(error);
  // Non-chunk errors go straight to the error screen (no flash). Chunk errors
  // start as 'pending' so the effect can decide reload-vs-error without ever
  // flashing the error screen during a routine deploy recovery.
  const [phase, setPhase] = useState<'pending' | 'error'>(chunk ? 'pending' : 'error');

  useEffect(() => {
    // The detail is for US — full object to the console, nothing to the DOM.
    // Never leak the message, stack, or module URL onto the customer's screen.
    console.error('[Raad] route error boundary caught:', error);
    if (!chunk) return;
    if (chunkErrorAction() === 'error') setPhase('error');
    // 'reloading' → location.reload() already called (guarded, once); the page
    // navigates away while the placeholder is shown.
  }, [error, chunk]);

  return phase === 'pending' ? <ReloadingPlaceholder /> : <ErrorScreen error={error} />;
}
